import { useState, useEffect, useCallback } from 'react';
import { Loader2, AlertTriangle, Save, CheckCircle, Lock, MessageCircle, Instagram, Globe, Users, Settings2 } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '../../lib/utils';
import { Card, CardContent } from '@easyflow/ui-frontend/components/ui/card';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Alert, AlertDescription } from '@easyflow/ui-frontend/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-frontend/components/ui/dialog';

interface MetaSender {
  sender_id: string;
  page_id: string;
  page_name: string | null;
  platform: 'messenger' | 'instagram';
  is_global: boolean;
  status: string;
}

interface TenantUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
}

interface SenderAssignment {
  user_id: string;
  source: 'manual' | 'meta_derived';
  username?: string;
  email?: string;
}

interface SenderConfig {
  is_global: boolean;
  assignments: SenderAssignment[];
}

function userLabel(u: TenantUser): string {
  const name = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
  return name || u.email;
}

/** Tab impostazioni per i canali Meta (Task #3214), una istanza per
 *  piattaforma (Messenger / Instagram Direct). Stessa UX di Telegram con una
 *  differenza: gli assegnatari derivati dalla connessione OAuth
 *  (`meta_derived`) non sono rimovibili da qui — la loro decadenza segue il
 *  lifecycle della connessione Meta. */
export default function MetaSettingsTab({ platform }: { platform: 'messenger' | 'instagram' }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [senders, setSenders] = useState<MetaSender[]>([]);
  const [users, setUsers] = useState<TenantUser[]>([]);
  // sender_id → flag "disponibile a tutti" (mappa is_global)
  const [assignGlobal, setAssignGlobal] = useState<Record<string, boolean>>({});
  // sender_id → utenti assegnati MANUALMENTE (i derivati sono a parte)
  const [assignData, setAssignData] = useState<Record<string, string[]>>({});
  // sender_id → utenti derivati (`meta_derived`), non rimovibili
  const [derivedData, setDerivedData] = useState<Record<string, string[]>>({});
  const [savingSenderId, setSavingSenderId] = useState<string | null>(null);
  // Dialog guidato "Cambia regime" (Task #3216): sender in editing + scelta corrente
  const [regimeSender, setRegimeSender] = useState<MetaSender | null>(null);
  const [regimeChoice, setRegimeChoice] = useState<'global' | 'restricted'>('global');
  const [regimeSaving, setRegimeSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sendersRes, usersRes] = await Promise.all([
        apiClient.get<{ senders: MetaSender[] }>('/messaging/meta-senders'),
        apiClient.get<TenantUser[]>('/messaging/sender-assignments/users'),
      ]);
      const senderList = (sendersRes.data?.senders ?? []).filter((s) => s.platform === platform);
      const userList = usersRes.data ?? [];
      setSenders(senderList);
      setUsers(userList);

      const configs = await Promise.all(
        senderList.map(async (s) => {
          const cfg = await apiClient.get<SenderConfig>(`/messaging/senders/${s.sender_id}/assignments`);
          return { senderId: s.sender_id, config: cfg.data };
        }),
      );
      const globalMap: Record<string, boolean> = {};
      const dataMap: Record<string, string[]> = {};
      const derivedMap: Record<string, string[]> = {};
      for (const entry of configs) {
        if (!entry.config) continue;
        globalMap[entry.senderId] = entry.config.is_global !== false;
        dataMap[entry.senderId] = entry.config.assignments
          .filter((a) => a.source === 'manual')
          .map((a) => a.user_id);
        derivedMap[entry.senderId] = entry.config.assignments
          .filter((a) => a.source === 'meta_derived')
          .map((a) => a.user_id);
      }
      setAssignGlobal(globalMap);
      setAssignData(dataMap);
      setDerivedData(derivedMap);
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err) || 'Caricamento pagine Meta fallito', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast, platform]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleAssignUser = (senderId: string, userId: string) => {
    // I derivati non si toccano da qui: il click su un derivato è disabilitato.
    if ((derivedData[senderId] ?? []).includes(userId)) return;
    setAssignData((prev) => {
      const current = prev[senderId] ?? [];
      const updated = current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId];
      return { ...prev, [senderId]: updated };
    });
  };

  const handleSave = async (senderId: string) => {
    const previousData = assignData[senderId] ?? [];
    setSavingSenderId(senderId);
    try {
      const isGlobal = assignGlobal[senderId] !== false;
      // Salva SOLO gli assegnatari manuali; il regime (is_global) si cambia
      // dal dialog guidato. Il backend tocca SOLO i `manual`: i derivati
      // (`meta_derived`) restano intatti comunque.
      await apiClient.put(`/messaging/senders/${senderId}/assignments`, { user_ids: previousData, is_global: isGlobal });
      toast({ title: 'Salvato', description: 'Assegnazione aggiornata' });
    } catch (err) {
      setAssignData((s) => ({ ...s, [senderId]: previousData }));
      toast({ title: 'Errore', description: getErrorMessage(err) || 'Salvataggio assegnazione fallito', variant: 'destructive' });
    } finally {
      setSavingSenderId(null);
    }
  };

  const openRegimeDialog = (sender: MetaSender) => {
    setRegimeChoice(assignGlobal[sender.sender_id] !== false ? 'global' : 'restricted');
    setRegimeSender(sender);
  };

  // Conferma il cambio regime: aggiorna SOLO is_global, preservando gli
  // assegnatari manuali correnti (il backend riscrive i `manual` con la lista
  // inviata e non tocca mai i `meta_derived`).
  const handleRegimeConfirm = async () => {
    if (!regimeSender) return;
    const senderId = regimeSender.sender_id;
    const newGlobal = regimeChoice === 'global';
    const manualIds = assignData[senderId] ?? [];
    setRegimeSaving(true);
    try {
      await apiClient.put(`/messaging/senders/${senderId}/assignments`, { user_ids: manualIds, is_global: newGlobal });
      setAssignGlobal((prev) => ({ ...prev, [senderId]: newGlobal }));
      setRegimeSender(null);
      toast({
        title: 'Regime aggiornato',
        description: newGlobal
          ? 'Le chat della Pagina sono ora disponibili a tutti gli utenti del tenant.'
          : 'Le chat della Pagina sono ora ristrette agli assegnatari.',
      });
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err) || 'Cambio regime fallito', variant: 'destructive' });
    } finally {
      setRegimeSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          {platform === 'instagram' ? (
            <Instagram className="w-5 h-5 text-pink-600" />
          ) : (
            <MessageCircle className="w-5 h-5 text-blue-600" />
          )}
          {platform === 'instagram' ? 'Instagram Direct' : 'Messenger'}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Per ogni Pagina scegli se le chat sono disponibili a tutti gli utenti del tenant
          oppure ristrette agli assegnatari. Gli utenti che hanno collegato la Pagina via
          Meta sono assegnatari automatici e non rimovibili da qui: decadono quando la loro
          connessione perde la Pagina. Il collegamento delle Pagine avviene da Integrazioni → Meta.
        </p>
      </div>

      {senders.length === 0 ? (
        <Alert>
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>
            Nessuna Pagina {platform === 'instagram' ? 'Instagram' : 'Messenger'} attiva per la
            messaggistica. Collega un account da Integrazioni → Meta e attendi il primo
            messaggio per creare il mittente.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-4">
          {senders.map((sender) => {
            const senderId = sender.sender_id;
            const isGlobal = assignGlobal[senderId] !== false;
            const assigned = assignData[senderId] ?? [];
            const derived = derivedData[senderId] ?? [];
            const saving = savingSenderId === senderId;
            return (
              <Card key={senderId} className="border">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {sender.platform === 'instagram' ? (
                        <Instagram className="w-4 h-4 text-pink-600 shrink-0" />
                      ) : (
                        <MessageCircle className="w-4 h-4 text-blue-600 shrink-0" />
                      )}
                      <span className="font-medium text-gray-900 truncate">
                        {sender.page_name || sender.page_id}
                      </span>
                      <Badge variant={sender.status === 'active' ? 'default' : 'secondary'}>{sender.status}</Badge>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {isGlobal ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Tutti</Badge>
                      ) : (
                        <Badge variant="outline">
                          {assigned.length + derived.length} utent{assigned.length + derived.length === 1 ? 'e' : 'i'}
                        </Badge>
                      )}
                      <Button size="sm" onClick={() => handleSave(senderId)} disabled={saving}>
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        <span className="ml-1">Salva</span>
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3 mb-3 p-2 rounded-md bg-muted border">
                    <div className="flex items-center gap-3 min-w-0">
                      {isGlobal ? (
                        <Globe className="w-4 h-4 text-green-600 shrink-0" />
                      ) : (
                        <Users className="w-4 h-4 text-blue-600 shrink-0" />
                      )}
                      <div className="text-sm min-w-0">
                        <div className="font-medium">
                          {isGlobal ? 'Regime: disponibile a tutto il tenant' : 'Regime: ristretta agli assegnatari'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {isGlobal
                            ? 'Ogni utente del tenant può vedere e rispondere alle chat di questa Pagina.'
                            : 'Solo gli assegnatari (automatici e selezionati qui sotto) vedono le chat.'}
                        </div>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="shrink-0" onClick={() => openRegimeDialog(sender)}>
                      <Settings2 className="h-4 w-4" />
                      <span className="ml-1">Cambia regime…</span>
                    </Button>
                  </div>
                  <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 ${isGlobal ? 'opacity-50 pointer-events-none' : ''}`}>
                    {users.map((u) => {
                      const isDerived = derived.includes(u.id);
                      const isSelected = isDerived || assigned.includes(u.id);
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => toggleAssignUser(senderId, u.id)}
                          disabled={isGlobal || isDerived}
                          title={isDerived ? 'Assegnatario automatico: ha collegato la Pagina via Meta' : undefined}
                          className={`flex items-center gap-2 p-2 rounded-md border text-sm transition-colors text-left ${
                            isSelected
                              ? 'bg-blue-50 border-blue-300 text-blue-800'
                              : 'bg-card border-border text-muted-foreground hover:bg-accent'
                          } ${isDerived ? 'cursor-default' : ''}`}
                        >
                          <div className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
                            isSelected ? 'bg-blue-600 border-blue-600' : 'border-border'
                          }`}>
                            {isDerived ? (
                              <Lock className="h-3 w-3 text-white" />
                            ) : (
                              isSelected && <CheckCircle className="h-3 w-3 text-white" />
                            )}
                          </div>
                          <div className="truncate">
                            <div className="font-medium truncate">{userLabel(u)}</div>
                            <div className="text-xs text-muted-foreground truncate">
                              {isDerived ? 'Automatico (connessione Meta)' : u.email}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={regimeSender !== null} onOpenChange={(open) => { if (!open && !regimeSaving) setRegimeSender(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Cambia regime chat — {regimeSender?.page_name || regimeSender?.page_id}
            </DialogTitle>
            <DialogDescription>
              Il regime decide chi vede e risponde alle chat di questa Pagina. Puoi cambiarlo
              in qualsiasi momento: gli assegnatari automatici (chi ha collegato la Pagina via
              Meta) e quelli selezionati manualmente restano memorizzati e tornano attivi se
              ripristini il regime ristretto.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setRegimeChoice('global')}
              className={`w-full flex items-start gap-3 p-3 rounded-md border text-left transition-colors ${
                regimeChoice === 'global'
                  ? 'bg-green-50 border-green-300'
                  : 'bg-card border-border hover:bg-accent'
              }`}
            >
              <Globe className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-sm">Disponibile a tutto il tenant</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Ogni utente del tenant vede e può rispondere alle chat della Pagina. Gli
                  assegnatari (automatici e manuali) restano memorizzati ma non limitano l'accesso.
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setRegimeChoice('restricted')}
              className={`w-full flex items-start gap-3 p-3 rounded-md border text-left transition-colors ${
                regimeChoice === 'restricted'
                  ? 'bg-blue-50 border-blue-300'
                  : 'bg-card border-border hover:bg-accent'
              }`}
            >
              <Users className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-sm">Ristretta agli assegnatari</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Solo gli assegnatari vedono le chat: chi ha collegato la Pagina via Meta
                  (automatico, non rimovibile da qui) più gli utenti che selezioni nella lista
                  della Pagina.
                </div>
              </div>
            </button>
          </div>

          {regimeSender && (
            <div className="text-xs text-muted-foreground">
              Assegnatari automatici attuali: {(derivedData[regimeSender.sender_id] ?? []).length} ·
              manuali: {(assignData[regimeSender.sender_id] ?? []).length}. Nessuno dei due viene
              modificato dal cambio regime.
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRegimeSender(null)} disabled={regimeSaving}>
              Annulla
            </Button>
            <Button onClick={handleRegimeConfirm} disabled={regimeSaving}>
              {regimeSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
              <span className="ml-1">Applica regime</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
